<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { PromotionData } from '~/composables/usePromotion'

const props = defineProps<{
    slide: Schemas['HeroSlide']
    index: number
}>()

const heroSlideEl = ref<HTMLDivElement | null>(null)

const promotionData: PromotionData | null = props.slide.promotion_name && props.slide.promotion_id && props.slide.creative_name && props.slide.creative_slot
    ? {
        promotion_name: props.slide.promotion_name,
        promotion_id: props.slide.promotion_id,
        creative_name: props.slide.creative_name,
        creative_slot: props.slide.creative_slot,
    }
    : null

const { selectPromotion, computedPromotionData } = usePromotionObserver(heroSlideEl, promotionData)

function onHeroElClick(event: MouseEvent) {
    if (computedPromotionData?.value) selectPromotion(computedPromotionData.value, event)
}

function hasContent(input: string | null | undefined): input is string {
    return input != null && input.trim() !== ''
}
</script>

<template>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions vuejs-accessibility/click-events-have-key-events -->
    <div
        ref="heroSlideEl"
        class="h-[650px] sm:h-[900px] md:h-[1029px] lg:h-[684px] w-full aspect-[2/3] md:aspect-[12/5] relative"
        @click="onHeroElClick($event)"
    >
        <div
            class="hero-slider-image-container relative h-full w-full"
            :class="{'after:content-[\'*\'] after:absolute after:bottom-0 after:left-0 after:h-4/5 md:after:h-1/2 after:w-full after:bg-[linear-gradient(180deg,rgba(0,0,0,0.00)0%,rgba(0,0,0,0.65)100%)]' : slide.isOverlayEnabled}"
        >
            <MiscImage
                v-if="slide.image"
                :src="slide.image.path"
                :alt="slide.image.translated?.alt || ''"
                width="3744"
                height="1560"
                sizes="640px sm:768px md:1024px lg:1280px xl:1536px 2xl:1920px"
                class="hero-slider-image h-full w-full object-cover"
                :lazy-load="index > 0"
                :preload="index === 0 ? { fetchPriority: 'high' } : false"
            />
        </div>

        <ClientOnly v-if="hasContent(slide.banderoleText1)">
            <StructureElementHeroSliderBanderole :slide="slide" />
            <template #fallback>
                <div
                    class="banderole absolute left-0 right-0 top-0 px-4 md:px-12 py-3"
                    :class="{
                        'bg-base-content': !slide.banderoleBgColor,
                        'uppercase': slide.banderoleTextAlLCaps
                    }"
                    :style="slide.banderoleBgColor ? {'background-color': slide.banderoleBgColor} : undefined"
                >
                    <div class="flex justify-between gap-6">
                        <MiscSkeleton size="large" :repeat="3" />
                    </div>
                </div>
            </template>
        </ClientOnly>

        <div v-if="slide.logo" class="absolute text-center left-0 right-0 m-auto bottom-16 mb-2">
            <MiscImage
                :src="slide.logo.path"
                :alt="slide.logo.translated?.alt || ''"
                lazy-load
                width="343"
                height="229"
                class="aspect-[3/2] object-cover m-auto"
            />
            <StructureElementHeroSliderCategories
                v-if="slide.useCategorySelect && slide.categories && slide.categories.length >= 1"
                :categories="slide.categories"
            />
            <button
                v-if="hasContent(slide.buttonText) && hasContent(slide.buttonTarget)"
                type="button"
                class="btn btn-md w-60 btn-accent btn-icon-right mt-6 px-4 rounded-xs"
            >
                {{ slide.buttonText }}
            </button>
        </div>

        <div v-else class="text-center absolute left-0 right-0 m-auto bottom-16 mb-2 min-w-[343px] max-w-[992px]">
            <div
                v-if="hasContent(slide.subtitle)"
                class="subtitle text-sm md:text-lg font-semibold text-sale-content pb-3 line-clamp-1"
            >
                {{ slide.subtitle }}
            </div>
            <div
                v-if="hasContent(slide.headlineLong)"
                class="heading text-5xl md:text-6.5xl font-normal text-sale-content uppercase font-fjalla-one px-4 line-clamp-2"
            >
                {{ slide.headlineLong }}
            </div>
            <div
                v-if="hasContent(slide.headline1)"
                class="heading text-8xl md:text-10xl font-normal text-sale-content uppercase font-fjalla-one px-4 line-clamp-1"
            >
                {{ slide.headline1 }}
            </div>
            <div
                v-if="hasContent(slide.headline2)"
                class="heading text-8xl md:text-10xl font-normal text-sale-content uppercase font-fjalla-one px-4 line-clamp-1"
            >
                {{ slide.headline2 }}
            </div>
            <div
                v-if="hasContent(slide.subtitleLong)"
                class="subtitle text-sm md:text-lg font-normal text-base-100 px-4 py-3"
            >
                {{ slide.subtitleLong }}
            </div>
            <StructureElementHeroSliderCategories
                v-if="slide.useCategorySelect && slide.categories && slide.categories.length >= 1"
                :categories="slide.categories"
            />
            <button
                v-if="hasContent(slide.buttonText) && hasContent(slide.buttonTarget)"
                type="button"
                class="btn slide-button btn-md w-60 btn-accent mt-6 px-4 rounded-xs"
            >
                {{ slide.buttonText }}
            </button>
        </div>
    </div>
</template>
